import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { MessageCircle, Send, ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 6000;

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
      ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);
  // Track whether the user is currently "pinned" to the bottom of the scroller.
  // Starts true so first-load / thread-switch always lands at the newest msg.
  const stickToBottomRef = useRef(true);
  // Force one jump-to-bottom whenever the thread changes (resets user's scroll pos).
  const [bottomTick, setBottomTick] = useState(0);
  // Tracks whether we've already done the first-load auto-select. Stops the
  // 6s poll from yanking the user back into a thread on mobile after they
  // tap "Back" to return to the thread list.
  const didAutoSelectRef = useRef(false);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/threads");
      setThreads(data);
      // Auto-select my own inbox ONCE on first load only. After that, respect
      // the user's choice (including a deliberate setActiveId(null) via Back).
      if (data.length > 0 && !didAutoSelectRef.current) {
        const ownThread = data.find((t) => t.is_own);
        setActiveId(ownThread?.thread_id || data[0].thread_id);
        didAutoSelectRef.current = true;
      }
    } catch { /* silent */ }
  }, []);

  const loadMessages = useCallback(async (tid) => {
    if (!tid) return;
    try {
      const { data } = await api.get(`/chat/threads/${tid}/messages`);
      // Capture scroll position BEFORE state update so we know whether to stick.
      const el = scrollRef.current;
      if (el) {
        const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
        stickToBottomRef.current = distance < 80; // within 80px of bottom = pinned
      }
      setMessages(data);
      // Mark as read (best-effort)
      api.post(`/chat/threads/${tid}/read`).catch(() => {});
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    const t = setInterval(loadThreads, POLL_MS);
    return () => clearInterval(t);
  }, [loadThreads]);
  useEffect(() => { if (activeId) loadMessages(activeId); }, [activeId, loadMessages]);
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(() => loadMessages(activeId), POLL_MS);
    return () => clearInterval(t);
  }, [activeId, loadMessages]);

  // Reset "stick to bottom" + jump to bottom whenever the user switches thread.
  useEffect(() => {
    stickToBottomRef.current = true;
    setBottomTick((n) => n + 1);
  }, [activeId]);

  // Auto-scroll to bottom only when the user was already near the bottom
  // (so they can scroll up to read older msgs without being yanked back).
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, bottomTick]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distance < 80;
  };

  const send = async () => {
    const content = draft.trim();
    if (!content || !activeId) return;
    try {
      const { data } = await api.post(`/chat/threads/${activeId}/messages`, { content });
      stickToBottomRef.current = true; // sending my own message should jump to bottom
      setMessages((m) => [...m, data]);
      setDraft("");
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // ---- Admin-only export helpers ----
  const downloadText = (filename, text) => {
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const formatThreadText = (thread, msgs) => {
    const lines = [];
    lines.push(`Mercy Management — Chat export`);
    lines.push(`Staff: ${thread?.staff_name || "—"}`);
    lines.push(`Exported: ${new Date().toLocaleString()}`);
    lines.push(`Messages: ${msgs.length}`);
    lines.push("─".repeat(48));
    lines.push("");
    msgs.forEach((m) => {
      const ts = m.created_at ? new Date(m.created_at).toLocaleString() : "";
      lines.push(`[${ts}] ${m.sender_name} (${m.sender_role}):`);
      lines.push(m.content);
      lines.push("");
    });
    return lines.join("\n");
  };

  const exportThread = (thread, msgs) => {
    if (!thread || !msgs || msgs.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const safe = (thread.staff_name || "thread").replace(/\s+/g, "_");
    const date = new Date().toISOString().slice(0, 10);
    downloadText(`chat-${safe}-${date}.txt`, formatThreadText(thread, msgs));
    toast.success(`Exported ${msgs.length} message${msgs.length === 1 ? "" : "s"}`);
  };

  const exportAll = async () => {
    if (threads.length === 0) { toast.error("No threads"); return; }
    toast.loading("Collecting all threads…", { id: "chat-export-all" });
    try {
      const results = await Promise.all(
        threads.map(async (t) => {
          const { data } = await api.get(`/chat/threads/${t.thread_id}/messages`);
          return { thread: t, msgs: data };
        })
      );
      const parts = [];
      parts.push(`Mercy Management — FULL Chat export`);
      parts.push(`Exported: ${new Date().toLocaleString()}`);
      parts.push(`Total threads: ${results.length}`);
      parts.push("═".repeat(48));
      parts.push("");
      results.forEach(({ thread, msgs }) => {
        parts.push(formatThreadText(thread, msgs));
        parts.push("═".repeat(48));
        parts.push("");
      });
      const date = new Date().toISOString().slice(0, 10);
      downloadText(`chat-all-threads-${date}.txt`, parts.join("\n"));
      toast.success(`Exported ${results.length} thread${results.length === 1 ? "" : "s"}`,
        { id: "chat-export-all" });
    } catch (e) {
      toast.error("Export failed", { id: "chat-export-all" });
    }
  };

  const activeThread = useMemo(
    () => threads.find((t) => t.thread_id === activeId) || null,
    [threads, activeId]
  );

  return (
    <Layout title="Chat">
      <div className="space-y-4" data-testid="chat-page">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-blue-400" /> Chat
          </h1>
          <div className="text-xs text-slate-500">
            {isAdmin
              ? "All staff requests land here. Reply to keep conversations in one place. You can also DM other admins."
              : "Send a message to your inbox (admins see it) or DM any admin directly."}
          </div>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3 min-h-[60vh]`}>
          {/* Thread list — every user (admin + staff) now sees every thread so anyone can DM anyone */}
          <div className={`rounded-lg border hairline surface-1 overflow-hidden ${activeId ? "hidden md:block" : ""}`}
              data-testid="chat-thread-list">
            <div className="text-xs uppercase tracking-wider text-slate-500 num px-3 py-2 border-b hairline flex items-center justify-between gap-2">
              <span>Threads · {threads.length}</span>
              {isAdmin && threads.length > 0 && (
                <button onClick={exportAll} data-testid="chat-export-all-btn"
                  className="text-[11px] normal-case tracking-normal px-2 py-0.5 rounded border border-slate-700 hover:border-blue-500 hover:text-blue-300 flex items-center gap-1">
                  <Download className="w-3 h-3" /> Export all
                </button>
              )}
            </div>
              <div className="max-h-[70vh] overflow-y-auto">
                {threads.length === 0 && (
                  <div className="text-xs text-slate-500 text-center py-8">No users yet.</div>
                )}
                {threads.map((t) => (
                  <button
                    key={t.thread_id}
                    onClick={() => setActiveId(t.thread_id)}
                    data-testid={`chat-thread-${t.staff_name}`}
                    className={`w-full text-left px-3 py-3 border-b border-slate-900/80 flex items-center gap-3 hover:bg-white/[0.02]
                      ${activeId === t.thread_id ? "bg-blue-500/10 border-l-2 border-l-blue-500" : ""}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: t.staff_color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                        <span className="truncate">
                          {t.staff_name}{t.is_own && <span className="text-slate-500 font-normal"> · you</span>}
                        </span>
                        {t.staff_role === "admin" && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 uppercase tracking-wider num leading-none">
                            Admin
                          </span>
                        )}
                        {t.unread > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 num">
                            {t.unread}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        {t.last_message || "No messages yet"}
                      </div>
                    </div>
                    {t.last_at && (
                      <span className="text-[10px] num text-slate-600 whitespace-nowrap">{fmtTime(t.last_at)}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

          {/* Conversation pane */}
          <div className={`rounded-lg border hairline surface-1 flex flex-col ${activeId ? "" : "hidden md:flex"}`}
            data-testid="chat-conversation">
            <div className="px-3 py-3 border-b hairline flex items-center gap-2">
              {activeId && (
                <Button
                  size="sm"
                  variant="outline"
                  className="md:hidden h-9 px-3 border-blue-500/50 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 shrink-0"
                  onClick={() => setActiveId(null)}
                  data-testid="chat-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full" style={{ background: activeThread?.staff_color || "#64748b" }} />
                <span className="text-sm font-medium truncate">
                  {activeThread
                    ? (activeThread.is_own
                        ? `${activeThread.staff_name} · your inbox`
                        : `${activeThread.staff_name}${activeThread.staff_role === "admin" ? " (Admin)" : ""}`)
                    : "Select a thread"}
                </span>
              </div>
              {isAdmin && activeId && messages.length > 0 && (
                <div className="ml-auto">
                  <Button size="sm" variant="outline" className="h-7 border-slate-700 text-xs"
                    onClick={() => exportThread(activeThread, messages)}
                    data-testid="chat-export-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> Export
                  </Button>
                </div>
              )}
            </div>

            <div ref={scrollRef} onScroll={handleScroll} className="flex-1 px-4 py-3 overflow-y-auto max-h-[60vh] min-h-[40vh]"
              data-testid="chat-messages">
              {!activeId && (
                <div className="text-sm text-slate-500 text-center py-20">
                  Select a thread on the left to open the conversation.
                </div>
              )}
              {activeId && messages.length === 0 && (
                <div className="text-sm text-slate-500 text-center py-10">
                  No messages yet. Say hello!
                </div>
              )}
              {messages.map((m) => {
                const mine = m.sender_id === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} mb-2`}
                    data-testid={`chat-msg-${m.id}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm
                      ${mine ? "bg-blue-600/90 text-white" : "surface-2 border hairline text-slate-200"}`}>
                      {!mine && (
                        <div className="text-[10px] uppercase tracking-wider mb-0.5 num"
                          style={{ color: m.sender_color }}>
                          {m.sender_name} · {m.sender_role}
                        </div>
                      )}
                      <div className="whitespace-pre-wrap break-words">{m.content}</div>
                      <div className={`text-[10px] mt-0.5 num ${mine ? "text-blue-100/80" : "text-slate-500"}`}>
                        {fmtTime(m.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {activeId && (
              <div className="border-t hairline p-2 flex items-end gap-2">
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={activeThread?.is_own
                    ? "Type a message to your inbox — admins will see it…"
                    : `Message ${activeThread?.staff_name || "user"}…`}
                  className="bg-[#0a0e1a] border-slate-800 min-h-[44px] max-h-[140px] text-sm flex-1"
                  data-testid="chat-draft"
                />
                <Button className="bg-blue-600 hover:bg-blue-500" onClick={send}
                  data-testid="chat-send-btn" disabled={!draft.trim()}>
                  <Send className="w-4 h-4 mr-1" /> Send
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
